// src/App.js
import {
  Routes, Route, Navigate,
} from "react-router-dom";
import "./App.css";

// Pages
import Home                 from "./Pages/Home";
import Uvunaji              from "./Pages/Uvunaji";
import Urithi               from "./Pages/Urithi";
import LoginPage            from "./Pages/Sigin";
import GeneralContributions from "./Pages/GeneralContributions";
import PrivacyPolicy        from "./Pages/PrivacyPolicy";

// Layout & auth
import Layout        from "./components/Layout";
import { useAuth }   from "./components/hooks/AuthContext";

// Lucide icons
import {
  Newspaper, Info, Briefcase, BookOpen, Image, ThumbsUp,
  Users, Calendar, HomeIcon, HandCoins, TreePalm, HandHeart,
  LogOut, Settings, HandHelping, Settings2, AwardIcon, ShieldCheck,
  CreditCard, BarChart2, Globe, UsersRound, UploadCloud, ShoppingBag,
  Mail, Video, History, Wallet, FileText, MessageSquare,
} from "lucide-react";

// MUI icons
import {
  ApprovalOutlined, AutoGraph, ListAltOutlined, Mood, MoodBad,
} from "@mui/icons-material";


// Feature components
import News                    from "./components/News";
import ForMore                 from "./components/ForMore";
import Portifolio              from "./components/Portifolio";
import Guide                   from "./components/Guide";
import Banner                  from "./components/Banner";
import Majanga                 from "./components/Majanga";
import Cheleko                 from "./components/Cheleko";
import WillingnessDashboard    from "./components/willingness";
import CorporatePage           from "./components/CorporatePage";
import ServiceRequestManager   from "./components/ServiceRequests";
import UserManagement          from "./components/userlist";
import SettingsConfiguration   from "./components/SettingsConfiguration";
import KileleniManagement      from "./components/Economically";
import MbelekoManagement       from "./components/Mbeleko";
import MbelekoBaseAmountConfig from "./components/mbeleko/MbelekoCOnfiguration";
import RewardPointsAward       from "./components/RewardPointsAward";
import MiNetAdmin              from "./components/MiNet";
import FinancialTrackerAdmin   from "./components/FinancialTracker";
import ContributionsLedger     from "./components/ContributionsLedger";
import WalletRequests          from "./components/WalletRequests";
import TeamManagement          from "./components/TeamManagement";
import WebsiteContent          from "./components/WebsiteContent";
import Messages               from "./components/Messages";
import Insights               from "./components/Insights";
import WebsiteUploads          from "./components/WebsiteUploads";
import WebsiteSettings         from "./components/WebsiteSettings";
import AuditLogs               from "./components/AuditLogs";
import GulioHubAdmin           from "./components/GulioHub";
import SellerDetailPage        from "./Pages/SellerDetailPage";
import Mailing                 from "./components/Mailing";

// ── Protected Route ────────────────────────────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#001f3f]">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-teal-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-teal-300 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return children;
};

// ── Root ───────────────────────────────────────────────────────────────────────
function App() {
  return (
    <Routes>
      <Route path="/login"          element={<LoginPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/*" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
    </Routes>
  );
}

// ── Authenticated shell ────────────────────────────────────────────────────────
function AppContent() {
  const { logout, user } = useAuth();

  const sidebarItems = [
    { label: "Home",     path: "/home",       icon: <HomeIcon  size={18} /> },
    { label: "News",     path: "/news",       icon: <Newspaper size={18} /> },
    { label: "For more", path: "/for-more",   icon: <Info      size={18} /> },
    { label: "Portfolio",path: "/portfolio",  icon: <Briefcase size={18} /> },
    { label: "Guide",    path: "/guide",      icon: <BookOpen  size={18} /> },
    { label: "Banners",  path: "/banners",    icon: <Image     size={18} /> },
    { label: "My Desire",path: "/willingness",icon: <ThumbsUp  size={18} /> },

    {
      label: "Uwezeshi",
      icon: <HandCoins size={18} />,
      children: [
        {
          label: "Economically",
          icon: <HandHeart size={18} />,
          children: [
            { label: "Kileleni", path: "/kileleni", icon: <AutoGraph size={18} /> },
            {
              label: "Mbeleko",
              icon: <HandHelping size={18} />,
              children: [
                { label: "Manage Mbeleko",        path: "/mbeleko",               icon: <ListAltOutlined fontSize="small" /> },
                { label: "Mbeleko Configuration", path: "/mbeleko-configuration", icon: <Settings2 size={18} /> },
              ],
            },
          ],
        },
        {
          label: "Socially",
          icon: <HandHeart size={18} />,
          children: [
            {
              label: "Majanga",
              icon: <MoodBad fontSize="small" />,
              children: [
                { label: "Dashboard",     path: "/majanga",                    icon: <ShieldCheck size={16} /> },
                { label: "Communities",   path: "/majanga?view=gangs",         icon: <MoodBad fontSize="small" /> },
                { label: "Configuration", path: "/majanga?view=configuration", icon: <Settings size={16} /> },
              ],
            },
            { label: "Cheleko",           path: "/cheleko",          icon: <Mood fontSize="small" /> },
            { label: "Service Requested", path: "/service-requests", icon: <ApprovalOutlined fontSize="small" /> },
          ],
        },
        { label: "CsR", path: "/csr", icon: <HandHeart size={18} /> },
      ],
    },

    { label: "Urithi",          path: "/urithi",        icon: <TreePalm  size={18} /> },
    { label: "Uvunaji",         path: "/uvunaji",        icon: <Calendar  size={18} /> },
    {
      label: "Miza Net",
      icon: <CreditCard size={18} />,
      children: [
        { label: "Overview & Stats",   path: "/mi-net",         icon: <CreditCard size={16} /> },
        { label: "Suspected Chats",    path: "/mi-net/flagged", icon: <ShieldCheck size={16} /> },
      ],
    },
    {
      label: "Financial Tracker",
      icon: <BarChart2 size={18} />,
      children: [
        { label: "Platform Overview",  path: "/financial-tracker",          icon: <BarChart2 size={16} /> },
        { label: "Personal Projects",  path: "/financial-tracker/projects", icon: <BarChart2 size={16} /> },
        { label: "Contributions Ledger", path: "/contributions-ledger",     icon: <CreditCard size={16} /> },
        { label: "Wallet Requests",    path: "/wallet-requests",            icon: <Wallet size={16} /> },
      ],
    },
    {
      label: "Website",
      icon: <Globe size={18} />,
      children: [
        { label: "Page Content", path: "/website/content", icon: <FileText    size={16} /> },
        { label: "Team",     path: "/website/team",     icon: <UsersRound   size={16} /> },
        { label: "Uploads",  path: "/website/uploads",  icon: <UploadCloud size={16} /> },
        { label: "Settings", path: "/website/settings", icon: <Video       size={16} /> },
      ],
    },
    { label: "GulioHub",        path: "/guliohub",       icon: <ShoppingBag size={18} /> },
    { label: "Messages",        path: "/messages",       icon: <MessageSquare size={18} /> },
    { label: "Mailing",         path: "/mailing",        icon: <Mail      size={18} /> },
    { label: "User Management", path: "/users",          icon: <Users     size={18} /> },
    { label: "Rewards",         path: "/rewards",        icon: <AwardIcon size={18} /> },
    { label: "Insights",        path: "/insights",       icon: <BarChart2 size={18} /> },
    { label: "Audit Logs",      path: "/audit-logs",     icon: <History   size={18} /> },
    { label: "Configuration",   path: "/configuration",  icon: <Settings  size={18} /> },
    { label: "Logout", icon: <LogOut size={18} />, onClick: logout },
  ];

  return (
    <Layout sidebarItems={sidebarItems}>
      {/* Top header bar — appears on every page */}
      <div className="px-6 pt-5 pb-3 bg-white border-b border-slate-100 flex items-center justify-between sticky top-0 z-10">
        <div>
          <h1 className="text-base font-bold text-slate-800 leading-tight">
            Welcome back,{" "}
            <span className="text-teal-600">{user?.name || "Admin"}</span>
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">JamboRicG Administration Portal</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
          <span className="text-xs text-slate-400">Live</span>
        </div>
      </div>

      {/* Page routes */}
      <Routes>
        <Route path="/"          element={<Navigate to="/home" replace />} />
        <Route path="/home"      element={<Home />} />
        <Route path="/news"      element={<News />} />
        <Route path="/for-more"  element={<ForMore />} />
        <Route path="/portfolio" element={<Portifolio />} />
        <Route path="/guide"     element={<Guide />} />
        <Route path="/banners"   element={<Banner />} />
        <Route path="/willingness" element={<WillingnessDashboard />} />

        <Route path="/csr"                   element={<CorporatePage />} />
        <Route path="/kileleni"              element={<KileleniManagement />} />
        <Route path="/mbeleko"               element={<MbelekoManagement />} />
        <Route path="/mbeleko-configuration" element={<MbelekoBaseAmountConfig />} />

        <Route path="/majanga"          element={<Majanga />} />
        <Route path="/cheleko"          element={<Cheleko />} />
        <Route path="/service-requests" element={<ServiceRequestManager />} />

        <Route path="/urithi"        element={<Urithi />} />
        <Route path="/uvunaji"       element={<Uvunaji />} />
        <Route path="/mi-net"                    element={<MiNetAdmin />} />
        <Route path="/mi-net/flagged"            element={<MiNetAdmin />} />
        <Route path="/financial-tracker"         element={<FinancialTrackerAdmin />} />
        <Route path="/financial-tracker/projects" element={<FinancialTrackerAdmin />} />
        <Route path="/contributions-ledger"      element={<ContributionsLedger />} />
        <Route path="/wallet-requests"           element={<WalletRequests />} />
        <Route path="/messages"         element={<Messages />} />
        <Route path="/insights"         element={<Insights />} />
        <Route path="/website/content"  element={<WebsiteContent />} />
        <Route path="/website/team"     element={<TeamManagement />} />
        <Route path="/website/uploads"  element={<WebsiteUploads />} />
        <Route path="/website/settings" element={<WebsiteSettings />} />
        <Route path="/guliohub"               element={<GulioHubAdmin />} />
        <Route path="/guliohub/sellers/:id"   element={<SellerDetailPage />} />
        <Route path="/mailing"       element={<Mailing />} />
        <Route path="/users"         element={<UserManagement />} />
        <Route path="/rewards"       element={<RewardPointsAward />} />
        <Route path="/audit-logs"    element={<AuditLogs />} />
        <Route path="/configuration" element={<SettingsConfiguration />} />
        <Route path="/general-contributions" element={<GeneralContributions />} />

        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;